import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { AdminProductItem } from "../hooks/useAdminProductsList";

// Wide enough for the longest known remark slug ("non-us-retail-restricted",
// 24 chars) on one line at the 10px mono body size.
const POPOVER_WIDTH = 240;
/** Gap between the anchor badge and the popover edge, in px. */
const ANCHOR_GAP = 6;
/** Minimum distance the popover keeps from the viewport edges, in px. */
const VIEWPORT_MARGIN = 8;

export interface RemarksPopoverTarget {
  product: AdminProductItem;
  /** Viewport-relative rect of the badge that opened the popover. */
  anchor: DOMRect;
}

interface ContractRemarksPopoverProps {
  target: RemarksPopoverTarget;
  onClose: () => void;
}

function clampLeft(anchor: DOMRect): number {
  // Centre on the anchor, then keep the whole popover inside the viewport.
  const centred = anchor.left + anchor.width / 2 - POPOVER_WIDTH / 2;
  const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  return Math.max(VIEWPORT_MARGIN, Math.min(centred, Math.max(VIEWPORT_MARGIN, maxLeft)));
}

const ContractRemarksPopover: React.FC<ContractRemarksPopoverProps> = ({ target, onClose }) => {
  const { product, anchor } = target;
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() => ({
    top: anchor.bottom + ANCHOR_GAP,
    left: clampLeft(anchor),
  }));

  // Flip above the badge when there is not enough room below. Runs before paint
  // so the popover never visibly jumps.
  useLayoutEffect(() => {
    const height = panelRef.current?.offsetHeight ?? 0;
    const below = anchor.bottom + ANCHOR_GAP;
    const fitsBelow = below + height + VIEWPORT_MARGIN <= window.innerHeight;
    setPosition({
      top: fitsBelow ? below : Math.max(VIEWPORT_MARGIN, anchor.top - ANCHOR_GAP - height),
      left: clampLeft(anchor),
    });
  }, [anchor]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Capture phase so scrolling any ancestor container (the page shell or the
    // horizontally scrollable table) dismisses the now-detached popover. The
    // panel's own remark list is a scroller too, and its events reach this
    // listener as well -- ignore those, or a product with enough remarks to
    // overflow could never be scrolled to read the rest.
    const onScroll = (e: Event) => {
      // `instanceof Node` matters: a window-level scroll targets `window`,
      // which is not a Node and would make contains() throw.
      const target = e.target;
      if (target instanceof Node && panelRef.current?.contains(target)) return;
      onClose();
    };
    const onResize = () => onClose();
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [onClose]);

  return (
    <>
      {/* Click-outside catcher */}
      <div className="fixed inset-0 z-40" onClick={onClose} data-testid="remarks-popover-backdrop" />
      <div
        ref={panelRef}
        role="dialog"
        aria-label="Contract remarks"
        style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
        className="fixed z-50 bg-rp-surface-raised border border-rp-separator rounded-lg shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-rp-separator">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-rp-grey/60">
            Contract Remarks
            {product.contractRemarks.length > 0 && (
              <span className="text-rp-grey/40"> · {product.contractRemarks.length}</span>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close contract remarks"
            className="text-rp-grey/50 hover:text-rp-grey"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* One remark per line -- slugs vary a lot in length, so a wrapped-chip
            layout reads poorly once a product carries ~10 of them. Caps at
            roughly 10 rows before scrolling. */}
        <div
          className="px-3 py-2 max-h-[232px] overflow-auto custom-scrollbar"
          data-testid="remarks-popover-list"
        >
          {product.contractRemarks.length === 0 ? (
            <p className="text-[11px] text-rp-grey/50">No contract remarks.</p>
          ) : (
            <ul className="space-y-1">
              {product.contractRemarks.map((remark) => (
                <li
                  key={remark}
                  className="flex items-center gap-1.5 text-[11px] font-mono text-rp-amber break-words"
                >
                  <span className="w-1 h-1 rounded-full bg-rp-amber/60 flex-shrink-0" />
                  {remark}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default ContractRemarksPopover;
